import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const API_BASE = (process.env.REACT_APP_API_BASE || 'https://backend.parthmerchant.com').replace(/\/$/, '');

// The visitor's own light/dark choice. Absent => follow the site's default mode.
const MODE_KEY = 'pm-theme-mode';
// Last-known palette pair, so a repeat visit paints the right colours on the
// first frame instead of flashing the defaults while the API responds.
const CACHE_KEY = 'pm-site-theme';
// Pre-theme-editor key: held a concrete palette id rather than a mode.
const LEGACY_KEY = 'pm-theme';

const ThemeContext = createContext();

// Every palette that has a `[data-theme="<id>"]` block in App.css, split by
// mode. The admin console picks one of each; these lists must stay in step with
// LIGHT_THEME_IDS / DARK_THEME_IDS in backend/src/landing.py and with
// admin/src/context/ThemeContext.js.
export const LIGHT_THEME_IDS = ['light-classic', 'paper-beige', 'snow'];
export const DARK_THEME_IDS = [
  'space-native', 'gruvbox', 'graphite', 'mono', 'slate', 'ash', 'cinder',
];

export const DEFAULT_SITE_THEME = {
  lightTheme: 'paper-beige',
  darkTheme: 'cinder',
  defaultMode: 'light',
};

export function isDarkTheme(id) {
  return DARK_THEME_IDS.includes(id);
}

function readCachedSiteTheme() {
  try {
    const raw = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
    if (raw && typeof raw === 'object') return { ...DEFAULT_SITE_THEME, ...raw };
  } catch { /* fall through */ }
  return DEFAULT_SITE_THEME;
}

// Read the visitor's mode, migrating the legacy palette-id key on first load:
// someone who had picked a dark palette should stay in dark mode.
function readMode() {
  try {
    const saved = localStorage.getItem(MODE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;

    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      const mode = isDarkTheme(legacy) || legacy === 'dark' ? 'dark' : 'light';
      localStorage.setItem(MODE_KEY, mode);
      localStorage.removeItem(LEGACY_KEY);
      return mode;
    }
  } catch { /* fall through */ }
  return null;
}

/**
 * Theme resolution has two independent inputs:
 *
 *   - the *palettes*, chosen site-wide in the admin console and served from
 *     /api/landing/content, and
 *   - the *mode* (light or dark), which is the visitor's own call and lives only
 *     on their device.
 *
 * So editing the theme in admin changes what light and dark look like for
 * everyone, without overriding anyone's light/dark preference. A visitor who has
 * never chosen gets the site's configured `defaultMode`.
 */
export function ThemeProvider({ children }) {
  const [siteTheme, setSiteTheme] = useState(readCachedSiteTheme);
  const [mode, setModeState] = useState(readMode);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const signal = typeof AbortSignal?.timeout === 'function'
          ? AbortSignal.timeout(4000)
          : undefined;
        const res = await fetch(`${API_BASE}/api/landing/content`, { signal });
        if (!res.ok) throw new Error(`${res.status}`);
        const data = await res.json();
        if (cancelled || !data?.theme) return;
        const next = { ...DEFAULT_SITE_THEME, ...data.theme };
        setSiteTheme(next);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(next)); } catch { /* noop */ }
      } catch {
        // Offline, slow, or a bad response — the cached pair (or the defaults)
        // is already applied, so there is nothing to do.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const isDark = (mode || siteTheme.defaultMode) === 'dark';
  const theme = isDark ? siteTheme.darkTheme : siteTheme.lightTheme;

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const setMode = useCallback((next) => {
    setModeState(next);
    try { localStorage.setItem(MODE_KEY, next); } catch { /* noop */ }
  }, []);

  const toggle = useCallback(() => setMode(isDark ? 'light' : 'dark'), [isDark, setMode]);

  const value = useMemo(() => ({
    theme, isDark, mode: mode || siteTheme.defaultMode,
    setMode, toggle, siteTheme,
  }), [theme, isDark, mode, siteTheme, setMode, toggle]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
